import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2, Eye, EyeOff, ArrowRight, User2, ShieldCheck, ShoppingCart, ChefHat, Receipt, Package, BarChart3 } from 'lucide-react';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: ShoppingCart, label: 'Orders',    gradient: 'from-orange-500 to-red-500' },
    { icon: ChefHat,      label: 'Kitchen',   gradient: 'from-amber-500 to-orange-600' },
    { icon: Receipt,      label: 'Billing',   gradient: 'from-red-500 to-rose-600' },
    { icon: Package,      label: 'Inventory', gradient: 'from-orange-600 to-amber-500' },
    { icon: BarChart3,    label: 'Reports',   gradient: 'from-rose-500 to-pink-600' },
  ];

  return (
    <div className="min-h-screen flex bg-[#FFF8F1]" data-testid="login-page">

      {/* ===================== LEFT — HERO ===================== */}
      <aside
        className="hidden lg:flex relative w-1/2 flex-col justify-between p-12 xl:p-16 overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(15,10,8,0.55) 0%, rgba(15,10,8,0.85) 100%), url(https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=1600&q=80)",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* subtle vignette overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-transparent to-orange-900/30 pointer-events-none" />

        {/* Headline */}
        <div className="relative z-10 max-w-md">
          <h2 className="text-white font-heading text-5xl xl:text-6xl font-extrabold leading-[1.1] tracking-tight">
            Run Your
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-red-500">Restaurant</span>
            <br />
            Faster
            <svg className="block mt-1 text-orange-500" width="160" height="12" viewBox="0 0 160 12" fill="none" aria-hidden="true">
              <path d="M2 8 Q 40 1, 80 6 T 158 5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
            </svg>
          </h2>
          <p className="mt-6 text-white/75 text-base max-w-sm leading-relaxed">
            All-in-one POS solution to manage your restaurant operations seamlessly.
          </p>

          {/* feature pills */}
          <div className="mt-8 flex flex-wrap gap-4">
            {features.map((f) => (
              <div key={f.label} className="flex flex-col items-center gap-1.5 group" data-testid={`feature-${f.label.toLowerCase()}`}>
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient} flex items-center justify-center shadow-lg shadow-orange-900/30 transition-transform group-hover:-translate-y-0.5`}>
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-white/80 text-xs font-medium">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom strip */}
        <div className="relative z-10 flex items-center gap-3 self-start rounded-2xl bg-black/40 backdrop-blur-md px-4 py-3 border border-white/10">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold leading-tight">Secure. Fast. Reliable.</p>
            <p className="text-white/60 text-xs leading-tight">Your restaurant, in perfect control.</p>
          </div>
        </div>

        {/* corner dots pattern */}
        <div className="absolute top-10 right-10 grid grid-cols-6 gap-2 opacity-30 pointer-events-none">
          {Array.from({ length: 24 }).map((_, i) => (
            <span key={i} className="w-1.5 h-1.5 rounded-full bg-orange-300" />
          ))}
        </div>
      </aside>

      {/* ===================== RIGHT — LOGIN ===================== */}
      <main className="relative flex-1 flex items-center justify-center p-6 sm:p-10">
        {/* decorative food line art (very faint) */}
        <svg className="absolute inset-0 w-full h-full text-orange-300/15 pointer-events-none" aria-hidden="true">
          <defs>
            <pattern id="dots" width="36" height="36" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.4" fill="currentColor" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)" />
        </svg>

        <div className="relative z-10 w-full max-w-md">
          {/* Mobile-only top bar (visible when left panel hidden) */}
          <div className="lg:hidden mb-6 text-center">
            <p className="text-xs uppercase tracking-widest text-orange-600 font-semibold">Welcome to</p>
          </div>

          <div
            className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-[0_25px_60px_-15px_rgba(234,88,12,0.25)] border border-orange-100/60 px-8 py-10"
            data-testid="login-card"
          >
            {/* Logo */}
            <div className="flex flex-col items-center mb-8">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-400 via-orange-500 to-red-500 flex items-center justify-center shadow-xl shadow-orange-500/40 mb-4 rotate-3 hover:rotate-0 transition-transform">
                <ChefHat className="w-10 h-10 text-white" strokeWidth={2.2} />
              </div>
              <h1 className="font-heading text-3xl font-extrabold text-slate-800 tracking-tight">
                Restro <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">POS</span>
              </h1>
              <p className="text-sm text-slate-500 mt-1.5">Smart Restaurant Operating System</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2" data-testid="login-error">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="email">Username or Email</label>
                <div className="relative">
                  <User2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    id="email"
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin"
                    required
                    className="w-full h-12 pl-11 pr-4 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-orange-400 focus:ring-4 focus:ring-orange-100 transition-all"
                    data-testid="login-email"
                    autoComplete="username"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="password">Password</label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full h-12 px-4 pr-12 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-orange-400 focus:ring-4 focus:ring-orange-100 transition-all"
                    data-testid="login-password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-orange-500 transition-colors p-1"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group w-full h-13 py-3.5 rounded-xl bg-gradient-to-r from-orange-500 via-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold shadow-lg shadow-orange-500/40 transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>

            <p className="mt-8 text-center text-xs text-slate-400">
              © {new Date().getFullYear()} Restro POS. All rights reserved.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
